import { Container, Graphics, Point } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';
import { RoomManager } from '@gameplay/management/rooms/RoomManager';
import { RoomCategory, RoomDefinition } from '@gameplay/management/rooms/RoomTypes';

export interface RoomSelectionCallbacks {
  onRoomSelectionChanged: (rooms: RoomDefinition[]) => void;
  onRoomCategoryApplied: (roomIds: string[], category: RoomCategory) => void;
}

export class RoomSelectionTool extends Container {
  private readonly gridSystem: GridSystem;
  private readonly roomManager: RoomManager;
  private callbacks: RoomSelectionCallbacks;
  
  private selectionGraphics: Graphics;
  private selectedRooms: Set<string> = new Set();
  private isSelecting: boolean = false;
  private startPosition: Point | null = null;
  private currentPosition: Point | null = null;
  private pendingCategory: RoomCategory | null = null;

  constructor(
    gridSystem: GridSystem, 
    roomManager: RoomManager, 
    callbacks: RoomSelectionCallbacks
  ) {
    super();
    this.gridSystem = gridSystem;
    this.roomManager = roomManager;
    this.callbacks = callbacks;

    this.selectionGraphics = new Graphics();
    this.addChild(this.selectionGraphics);
    
    this.setupInteraction();
  }

  private setupInteraction(): void {
    this.eventMode = 'static';
    this.hitArea = { contains: () => true } as any; // Cover entire screen
    
    this.on('pointerdown', this.onPointerDown.bind(this));
    this.on('pointermove', this.onPointerMove.bind(this));
    this.on('pointerup', this.onPointerUp.bind(this));
    this.on('pointerupoutside', this.onPointerUp.bind(this));
  }

  private onPointerDown(event: any): void {
    if (!this.visible) return;
    
    const worldPos = event.global;
    this.startPosition = new Point(worldPos.x, worldPos.y);
    this.currentPosition = new Point(worldPos.x, worldPos.y);
    this.isSelecting = true;
    
    this.clearSelection();
    this.updateSelectionVisual();
  }

  private onPointerMove(event: any): void {
    if (!this.isSelecting || !this.startPosition) return;
    
    const worldPos = event.global;
    this.currentPosition = new Point(worldPos.x, worldPos.y);
    
    this.updateSelection();
    this.updateSelectionVisual();
  }

  private onPointerUp(): void {
    if (!this.isSelecting) return;
    
    this.isSelecting = false;
    this.applySelection();
    
    // Clear visual selection after a delay
    setTimeout(() => {
      this.selectionGraphics.clear();
    }, 500);
  }

  private updateSelection(): void {
    if (!this.startPosition || !this.currentPosition) return;

    // Convert screen coordinates to grid coordinates
    const startGrid = this.screenToGrid(this.startPosition);
    const endGrid = this.screenToGrid(this.currentPosition);

    // Define selection rectangle in grid space
    const minX = Math.min(startGrid.x, endGrid.x);
    const maxX = Math.max(startGrid.x, endGrid.x);
    const minY = Math.min(startGrid.y, endGrid.y);
    const maxY = Math.max(startGrid.y, endGrid.y);

    // Find all rooms that intersect with selection rectangle
    this.selectedRooms.clear();
    
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const room = this.roomManager.getRoomAt(x, y, 0); // Assume floor 0 for now
        if (room) {
          this.selectedRooms.add(room.id);
        }
      }
    }
  }

  private updateSelectionVisual(): void {
    this.selectionGraphics.clear();
    
    if (!this.startPosition || !this.currentPosition) return;

    // Simplified selection - just show selected room highlights without the rectangle
    this.selectedRooms.forEach(roomId => {
      const room = this.roomManager.getRoom(roomId);
      if (room) {
        this.highlightRoom(room);
      }
    });
  }

  private highlightRoom(room: RoomDefinition): void {
    const highlightColor = this.getCategoryColor(room.category);
    
    room.tiles.forEach(tile => {
      const worldPos = this.gridSystem.gridToWorld(tile.gridX, tile.gridY);
      
      this.selectionGraphics.lineStyle(3, highlightColor, 0.8);
      this.selectionGraphics.beginFill(highlightColor, 0.3);
      
      // Draw isometric diamond shape
      this.selectionGraphics.drawPolygon([
        worldPos.x - GridSystem.TILE_WIDTH / 2, worldPos.y,
        worldPos.x, worldPos.y - GridSystem.TILE_HEIGHT / 2,
        worldPos.x + GridSystem.TILE_WIDTH / 2, worldPos.y,
        worldPos.x, worldPos.y + GridSystem.TILE_HEIGHT / 2
      ]);
      
      this.selectionGraphics.endFill();
    });
  }

  private screenToGrid(screenPos: Point): Point {
    // This is a simplified conversion - would need proper camera transformation
    // For now, assume 1:1 mapping for demonstration
    const worldX = screenPos.x;
    const worldY = screenPos.y;
    
    // Convert world coordinates to grid coordinates using the isometric formulas
    const gridX = Math.floor((worldX / (GridSystem.TILE_WIDTH / 2) + worldY / (GridSystem.TILE_HEIGHT / 2)) / 2);
    const gridY = Math.floor((worldY / (GridSystem.TILE_HEIGHT / 2) - worldX / (GridSystem.TILE_WIDTH / 2)) / 2);
    
    return new Point(gridX, gridY);
  }

  private getCategoryColor(category: RoomCategory): number {
    const colors: Record<RoomCategory, number> = {
      'bedroom': 0x4CAF50,
      'bathroom': 0x2196F3,
      'dining': 0xFF9800,
      'medical': 0xF44336,
      'recreation': 0x9C27B0,
      'utility': 0x607D8B,
      'unspecified': 0x424242
    };
    return colors[category] || 0x424242;
  }

  private applySelection(): void {
    if (this.pendingCategory && this.selectedRooms.size > 0) {
      // Apply category to selected rooms
      const roomIds = Array.from(this.selectedRooms);
      this.callbacks.onRoomCategoryApplied(roomIds, this.pendingCategory);
      this.pendingCategory = null;
    }

    // Notify about selection change
    const selectedRoomObjects = Array.from(this.selectedRooms)
      .map(id => this.roomManager.getRoom(id))
      .filter(room => room !== null) as RoomDefinition[];
      
    this.callbacks.onRoomSelectionChanged(selectedRoomObjects);
  }

  public clearSelection(): void {
    this.selectedRooms.clear();
    this.selectionGraphics.clear();
    this.callbacks.onRoomSelectionChanged([]);
  }

  public setPendingCategory(category: RoomCategory): void {
    this.pendingCategory = category;
  }

  public enable(): void {
    this.visible = true;
    this.eventMode = 'static';
  }

  public disable(): void {
    this.visible = false;
    this.eventMode = 'none';
    this.clearSelection();
    this.selectionGraphics.clear();
  }
}